import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Grid, Stack, Typography, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { solidColors } from "../../../constants/segmentTools";
import { usePaletteColor } from "../../../hooks/usePaletteColor";
import { useIfUserEligible } from "../../../hooks/useIfUserEligible";
import { FEATURES } from "../../../constants/features";
import FeatureEligibility from "../../FeatureEligibility";
import TierFeatureBadge from "../../TierFeatureBadge";
import {
  resolveRequiredTier,
  resolveTierName,
} from "../../../utils/tierTooltip";
import {
  addCustomColorNewAction,
  deleteCustomColorAction,
  editCustomColorAction,
} from "../../../redux/actions/categoryColors";
import CustomAccordion from "../../Accordion";
import CustomColorPicker from "../../CustomColorPicker";
import ColorPalette from "../../SegmentBuilder/Shared/ColorPalette";
import ColorSwatch from "../../Swatch";
import TransparentSwatch from "../../backgroundPatterns/TransparentSwatch";
import { TRANSPARENT_PRESET_ENABLED } from "../../../constants/featureFlags";
import Tabs from "../../Tabs";
import { tabsVariantFor } from "../../Tabs/tabsVariant";
import WheelPaletteTab from "../../SegmentBuilder/Main/WheelPaletteTab";

const SegmentColor = ({
  showTabs = true,
  title = "Segment Color",
  onColorChange = () => { },
  currentColor = "green",
  isExpanded = false,
  showSuggestedColorPalette, // This prop now correctly controls the Segment Palette tab
  // Gordon 2026-07-20 (REACT_APP_TRANSPARENT_PRESET): whether the shared
  // Transparent swatch is offered in the Preset row. Defaults FALSE because
  // <SegmentColor> is the WHEEL-ANATOMY picker — Segment Color / Segment Fill /
  // Segment & Wheel Wallpaper are on Gordon's binding exclusion list (the
  // wheel's own anatomy is never transparent). So even with the flag ON this
  // picker shows no swatch unless a future non-anatomy consumer opts in.
  allowTransparent = false,
}) => {
  const initialInnerTabs = ["Preset", "Custom", "Wheel Palette"]; // Base tabs for inner color selection
  const categories = ["Solid", "Gradient", "Wallpaper"]; // Main categories

  const [colorsTab, setColorsTab] = useState(initialInnerTabs); // State for the inner color tabs
  const [activeColorTab, setActiveColorTab] = useState(initialInnerTabs[0]); // Initialize with the first tab
  const [expanded, setExpanded] = useState(isExpanded);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [selectedColor, setSelectedColor] = useState(currentColor);
  const [temporaryColor, setTemporaryColor] = useState("#FFFFFF");
  const [colorPickerVisible, setColorPickerVisible] = useState(false);

  const {
    isEligible: isEligibleCustomColors,
    isCheckingEligibility: isCheckingEligibilityCustomColors,
    subscription: customColorsTierName,
    requiredSubscription: customColorsTier,
  } = useIfUserEligible(FEATURES.CUSTOM_HEX_COLORS);

  const {
    isEligible: isEligibleWheelPalette,
    isCheckingEligibility: isCheckingEligibilityWheelPalette,
    subscription: wheelPaletteTierName,
    requiredSubscription: wheelPaletteTier,
  } = useIfUserEligible(FEATURES.ADV_PALLETE_GENERATOR);
  const { getPaletteColor } = usePaletteColor();
  const dispatch = useDispatch();

  const myColors = useSelector((state) => state.categoryColors.myCustomColors);
  const [editColorMode, setEditColorMode] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState(null);

  // Effect to dynamically update the inner color tabs based on showSuggestedColorPalette
  useEffect(() => {
    // If 'showSuggestedColorPalette' is true, add 'Segment Palette' to the tabs
    // Otherwise, revert to the initial set of tabs
    const newTabs = showSuggestedColorPalette
      ? [...initialInnerTabs, "Segment Palette"]
      : initialInnerTabs;
    setColorsTab(newTabs);

    // If the currently active tab is 'Segment Palette' and it's no longer allowed,
    // default back to 'Preset' to avoid a broken UI state.
    if (!newTabs.includes(activeColorTab)) {
      setActiveColorTab("Preset");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColorTab, showSuggestedColorPalette]);

  useEffect(() => {
    setSelectedColor(currentColor);
  }, [currentColor]);


  const handleColorTabChange = (_, newValue) => {
    setActiveColorTab(newValue);
  };

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded ? !expanded : false);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    onColorChange && onColorChange(color);
  };

  const handleCustomColorPicker = () => {
    setEditColorMode(false);
    setColorPickerVisible(true);
  };

  const handleTemporaryColorChange = (color) => {
    setTemporaryColor(color);
  };

  const onConfirmCustomColor = () => {
    const existingColor = myColors.find(
      (color) => color.color.color_code === temporaryColor
    );
    if (existingColor) {
      setSelectedColorId(existingColor?.id);
      handleColorChange(existingColor?.color?.color_code);
      setEditColorMode(true);
    } else {
      if (editColorMode) {
        dispatch(
          editCustomColorAction(selectedColorId, temporaryColor, (data) => {
            setSelectedColorId(data?.id);
            handleColorChange(data?.color?.color_code);
            setEditColorMode(true);
          })
        );
      } else {
        dispatch(
          addCustomColorNewAction(temporaryColor, (data) => {
            setSelectedColorId(data?.id);
            handleColorChange(data?.color?.color_code);
            setEditColorMode(true);
          })
        );
      }
    }
    setColorPickerVisible(false);
  };

  const handleColorEditClick = () => {
    setEditColorMode(true);
    setTemporaryColor(selectedColor);
    setColorPickerVisible(true);
  };

  const handleDeleteCustomColor = () => {
    dispatch(
      deleteCustomColorAction(selectedColorId, () => {
        setSelectedColorId(null); // Changed from "" to null for consistency with state init
        setSelectedColor("");
        setEditColorMode(false);
      })
    );
  };

  const renderCustomColorPalette = () => {
    return myColors.map((color) => (
      <Grid item key={color.id}>
        <Box
          onClick={() => {
            setEditColorMode(true);
            setSelectedColorId(color.id);
            handleColorChange(color.color.color_code);
          }}
        >
          <ColorSwatch
            color={color.color.color_code}
            borderColor={
              selectedColor === color.color.color_code ? "#BBBBBB" : undefined
            }
          />
        </Box>
      </Grid>
    ));
  };

  const renderColorTabs = () => {
    const categoriesWithIcons = colorsTab.map((category) => {
      const config = {
        label: category,
        value: category,
      };

      // Apply feature eligibility tooltip for "Custom" and "Wheel Palette" tabs.
      // These are TWO DIFFERENT gates (CUSTOM_HEX_COLORS vs
      // ADV_PALLETE_GENERATOR) that a single hardcoded string used to describe
      // identically — each tab now names the tier ITS own entitlement returned.
      const isGatedCustom = category === "Custom" && !isEligibleCustomColors;
      const isGatedWheelPalette =
        category === "Wheel Palette" && !isEligibleWheelPalette;

      if (isGatedCustom || isGatedWheelPalette) {
        config.customIcon = (
          <TierFeatureBadge
            requiredSubscription={
              isGatedCustom ? customColorsTier : wheelPaletteTier
            }
            subscription={
              isGatedCustom ? customColorsTierName : wheelPaletteTierName
            }
            placement="bottom"
            size="20px"
          />
        );
      }
      return config;
    });

    return (
      <Grid item xs={12}>
        <Tabs
          categoriesWithIcons={categoriesWithIcons}
          selectedCategory={activeColorTab}
          onCategoryChange={handleColorTabChange}
          color="#81459B"
          indicatorColor="#C283DA"
          // threshold=2: this strip can land at exactly 3 tabs
          // (Preset/Custom/Wheel Palette, Segment Palette absent) with
          // "Wheel Palette" still too wide for a fullWidth three-way split
          // (p12-wheel-palette-tab-label-cut-by-9px) — see tabsVariant.js.
          variant={tabsVariantFor(colorsTab, 2)}
          scrollButtons="auto"
          allowScrollButtonsMobile
          // vy-0914-ew-palette-source-strip-hard-clip: scrollable alone
          // leaves the scroller's own overflow:hidden edge as a hard cut —
          // MEASURED live (1440px) "Wheel Palette" showing 53.9 of its
          // 113.3px with zero fade. Same always-on both-edge fade already
          // blessed for this failure class (wallpaperTabProps.js /
          // vy-0914-ew-wallpaper-strip-left-edge-hard-clip) — fade both
          // ends, not just the one clipped at rest, since selecting a
          // later tab scrolls the strip and can clip the other edge too.
          sx={{
            "& .MuiTabs-scroller": {
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)",
              maskImage:
                "linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)",
            },
          }}
        />
      </Grid>
    );
  };

  // Main rendering logic for the component content (used by both accordion and non-accordion)
  const content = (
    <Box sx={{ padding: 2, paddingTop: showTabs ? 0 : 2 }}>
      {showTabs && ( // Show main category tabs if showTabs prop is true
        <Tabs
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          color={"#81459B"}
          indicatorColor="#C283DA"
        />
      )}
      {selectedCategory === "Solid" && (
        <Box>
          {/* Render the inner color tabs here */}
          {renderColorTabs()}

          {activeColorTab === colorsTab[0] ? ( // Preset Tab
            <Grid container mt={1} spacing={1} alignItems="center">
              {TRANSPARENT_PRESET_ENABLED && allowTransparent && (
                <Grid item>
                  <TransparentSwatch
                    size={32}
                    selected={
                      String(selectedColor).toLowerCase() === "transparent"
                    }
                    onSelect={() => {
                      handleColorChange("transparent");
                      setEditColorMode(false);
                      setSelectedColorId(null);
                    }}
                  />
                </Grid>
              )}
              {solidColors.map((color) => (
                <Grid item key={color}>
                  <Box
                    onClick={() => {
                      handleColorChange(color);
                      setEditColorMode(false);
                      setSelectedColorId(null);
                    }}
                  >
                    <ColorSwatch
                      color={color}
                      borderColor={selectedColor === color && "#BBBBBB"}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : activeColorTab === colorsTab[1] ? ( // Custom Tab
            isCheckingEligibilityCustomColors ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress size={40} />
              </Box>
            ) : !isEligibleCustomColors ? (
              <FeatureEligibility
                subscription={resolveTierName(
                  customColorsTier,
                  customColorsTierName
                )}
                requiredSubscription={resolveRequiredTier(
                  customColorsTier,
                  customColorsTierName
                )}
              />
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Stack
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    mr={5}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        fontFamily: "DM Sans",
                      }}
                    >
                      Add Custom Colors
                    </Typography>
                  </Stack>
                  <Stack
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    {editColorMode && (
                      <EditOutlinedIcon
                        onClick={handleColorEditClick}
                        style={{
                          fontSize: "22px",
                          cursor: "pointer",
                          marginRight: "3px",
                          color: "#625B71",
                        }}
                      />
                    )}

                    {editColorMode && (
                      <DeleteOutlineIcon
                        onClick={handleDeleteCustomColor}
                        style={{
                          fontSize: "23px",
                          cursor: "pointer",
                          marginRight: "3px",
                          color: "#625B71",
                        }}
                      />
                    )}

                    <AddCircleOutlineOutlinedIcon
                      sx={{ color: "#625B71", cursor: "pointer" }}
                      onClick={() => {
                        handleCustomColorPicker();
                      }}
                    />
                  </Stack>
                </Box>
                {!myColors.length && (
                  <Typography
                    sx={{
                      color: "#BBBBBB",
                      mt: 1,
                      fontSize: "12px",
                      fontFamily: "DM Sans",
                    }}
                  >
                    No custom colors yet — add your first one!
                  </Typography>
                )}
                <Grid container spacing={1} alignItems="center">
                  {renderCustomColorPalette()}
                </Grid>
                {colorPickerVisible && (
                  <CustomColorPicker
                    customStyle={{
                      position: "fixed",
                      left: "70.5vw",
                      bottom: "14vh",
                    }}
                    visible={colorPickerVisible}
                    onClose={() => setColorPickerVisible(false)}
                    onConfirm={onConfirmCustomColor}
                    color={temporaryColor}
                    onColorChange={handleTemporaryColorChange}
                  />
                )}
              </Box>
            )
          ) : activeColorTab === colorsTab[2] ? ( // Wheel Palette Tab
            isCheckingEligibilityWheelPalette ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress size={40} />
              </Box>
            ) : !isEligibleWheelPalette ? (
              <FeatureEligibility
                subscription={resolveTierName(
                  wheelPaletteTier,
                  wheelPaletteTierName
                )}
                requiredSubscription={resolveRequiredTier(
                  wheelPaletteTier,
                  wheelPaletteTierName
                )}
              />
            ) : (
              <WheelPaletteTab
                handleSelectColor={(color) =>
                  handleColorChange(`wheel_suggested_color_${color}`)
                }
                selectedColor={selectedColor}
                showButtonAndTitle={true}
              />
            )
          ) : activeColorTab === "Segment Palette" &&
            showSuggestedColorPalette ? ( // Segment Palette Tab (NEW)
            <div style={{ padding: "10px 0px" }}>
              <ColorPalette
                handleSelectColor={(color) =>
                  handleColorChange(`suggested_color_${color}`)
                }
                selectedColor={selectedColor}
              />
            </div>
          ) : null}
        </Box>
      )}
      {/* Additional categories like Gradient, Wallpaper would go here if needed */}
    </Box>
  );

  if (!isExpanded) {
    return (
      <CustomAccordion
        title={title}
        icon={
          selectedColor ? (
            <ColorSwatch
              color={getPaletteColor(selectedColor)}
              borderColor={"#79747E"}
              isAccordionIcon
            />
          ) : (
            <BlockIcon />
          )
        }
        expanded={expanded}
        onChange={handleChange}
      >
        {content} {/* Render the shared content */}
      </CustomAccordion>
    );
  }

  return (
    <>
      {content} {/* Render the shared content directly when not in accordion */}
    </>
  );
};

export default SegmentColor;